import * as React from 'react';

import { Box, Tooltip } from '@mui/material';

import { useCopy } from './useCopy';

/**
 * Inline text that copies itself.
 *
 * Used for version ids: the id is the only handle on a run once it leaves this
 * screen — it names the EFS artifact directory and keys the
 * perception_model_job row — and it is long enough that retyping it is how
 * people end up looking at the wrong model.
 *
 * A real button rather than a click handler on a span, so it is reachable by
 * keyboard and announced as something you can activate.
 */

type Props = {
  value: string;
  /** what to render, when it differs from the value being copied */
  children?: React.ReactNode;
  title?: string;
};

const CopyText: React.FC<Props> = ({ value, children, title }) => {
  const { copied, copy } = useCopy();

  return (
    // describeChild: without it MUI makes the tooltip the button's accessible
    // name, so the version id — the only thing worth reading here — is replaced
    // by "Click to copy" for anyone using a screen reader.
    <Tooltip title={copied ? 'Copied' : title || 'Click to copy'} describeChild>
      <Box
        component="button"
        type="button"
        onClick={() => copy(value)}
        sx={{
          p: 0,
          border: 0,
          background: 'none',
          font: 'inherit',
          color: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          textDecorationLine: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '2px',
          '&:hover': { color: 'primary.main' },
        }}
      >
        {children ?? value}
      </Box>
    </Tooltip>
  );
};

export default CopyText;
